import React, { useState, useEffect } from 'react';
import { Mail, Send, Users, Clock, CheckCircle, Settings } from 'lucide-react';
import { Member, Town } from '../../types';
import { emailApi, settingsApi, templateApi } from '../../services/api';
import { useGlobalToast } from '../../contexts/ToastContext';
import { useConfirmation } from '../../hooks/useConfirmation';
import { ConfirmationModal } from '../Common/ConfirmationModal';
import { darkModeClasses, getInfoPanelClasses } from '../../utils/darkMode';
import { useAuth } from '../../contexts/AuthContext';
import { HelpButton } from '../Help/HelpButton';

interface EmailCenterProps {
  members: Member[];
  towns: Town[];
  selectedTownId: string;
}

export const EmailCenter: React.FC<EmailCenterProps> = ({
  members,
  towns,
  selectedTownId
}) => {
  interface EmailHistoryItem {
    id: string;
    subject: string;
    message: string;
    recipients: number;
    date: string;
  }

  interface EmailStats {
    thisMonth: number;
    totalSent: number;
    deliveryRate: number;
    totalCost: number;
  }

  interface EmailTemplate {
    id: string;
    name: string;
    subject?: string;
    content?: string;
  }

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('selected-town');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [messageType, setMessageType] = useState('general');
  const [recentEmails, setRecentEmails] = useState<EmailHistoryItem[]>([]);
  const [emailStats, setEmailStats] = useState<EmailStats>({ thisMonth: 0, totalSent: 0, deliveryRate: 0, totalCost: 0.00 });
  const [emailConfig, setEmailConfig] = useState({ provider: 'resend', configured: false });
  const [availableTemplates, setAvailableTemplates] = useState<EmailTemplate[]>([]);
  const { success, error: toastError } = useGlobalToast();
  const { isOpen, options, confirm, handleConfirm, handleCancel } = useConfirmation();

  useEffect(() => {
    let mounted = true;

    // Load settings
    settingsApi.get().then((res) => {
      const data = res?.data as { settings?: { adminContact?: string }, adminContact?: string } | undefined;
      const contact = data?.settings?.adminContact || data?.adminContact;
      if (contact && mounted) {
        // adminContact state was unused, so we're just loading it for now.
        // If needed in the future, we can add it back.
        console.log('Admin contact loaded:', contact);
      }
    }).catch(() => { });

    // Load email history and stats
    emailApi.getHistory().then((res) => {
      if (res.success && mounted && res.data) {
        setRecentEmails((res.data.emails as unknown as EmailHistoryItem[]) || []);
        setEmailStats((res.data.stats as unknown as EmailStats) || { thisMonth: 0, totalSent: 0, deliveryRate: 0, totalCost: 0 });
        setEmailConfig((res.data.config as unknown as { provider: string, configured: boolean }) || { provider: 'resend', configured: false });
      }
    }).catch(() => {
      setRecentEmails([]);
      setEmailStats({ thisMonth: 0, totalSent: 0, deliveryRate: 0, totalCost: 0 });
    });

    // Load templates
    templateApi.getAll('email').then((res) => {
      if (res.success && res.data && mounted) {
        setAvailableTemplates(res.data as unknown as EmailTemplate[]);
      }
    }).catch(err => console.error('Failed to load email templates', err));

    return () => { mounted = false; };
  }, []);

  const { user: _user } = useAuth();

  const filteredMembers = selectedTownId === 'all'
    ? members.filter(m => m.email && m.status !== 'deceased') // Only active members with email addresses
    : members.filter(m => m.townId === selectedTownId && m.email && m.status !== 'deceased');

  const selectedTownName = (() => {
    if (selectedTownId === 'no-town-assigned') {
      return 'No Town Assigned';
    }
    if (selectedTownId === 'all') {
      return 'All Towns';
    }
    return towns.find(t => t.id === selectedTownId)?.name || 'Unknown Town';
  })();

  const getRecipientCount = () => {
    switch (recipientType) {
      case 'all-towns':
        return members.filter(m => m.email && m.status !== 'deceased').length;
      case 'selected-town':
        return filteredMembers.length;
      case 'selected-members':
        return selectedMembers.filter(id => {
          const member = members.find(m => m.id === id);
          return member?.email && member.status !== 'deceased';
        }).length;
      default:
        return 0;
    }
  };


  const handleTemplateChange = (templateId: string) => {
    setMessageType(templateId);

    if (templateId === 'general') {
      setSubject('');
      setMessage('');
      return;
    }

    const selectedTemplate = availableTemplates.find(t => t.id === templateId);
    if (selectedTemplate) {
      setSubject(selectedTemplate.subject || '');
      setMessage(selectedTemplate.content || '');
    }
  };

  const handleSendEmail = async () => {
    const recipientCount = getRecipientCount();

    const confirmed = await confirm({
      title: 'Send Email',
      message: `Send email to ${recipientCount} recipients with valid email addresses?`,
      confirmText: 'Send Email',
      type: 'info'
    });

    if (confirmed) {
      try {
        let recipients: string[] = [];

        switch (recipientType) {
          case 'all-towns':
            recipients = members.filter(m => m.status !== 'deceased').map(m => m.email).filter(Boolean) as string[];
            break;
          case 'selected-town':
            recipients = filteredMembers.map(m => m.email).filter(Boolean) as string[];
            break;
          case 'selected-members':
            recipients = selectedMembers.map(id => {
              const member = members.find(m => m.id === id);
              return member?.email;
            }).filter(Boolean) as string[];
            break;
        }

        const response = await emailApi.sendBulk({
          subject,
          message,
          recipients,
          type: messageType
        });

        if (response.success) {
          success(`Email sent successfully to ${recipients.length} recipients!`);
          setSubject('');
          setMessage('');
          setSelectedMembers([]);
        } else {
          toastError('Failed to send email: ' + response.error);
        }
      } catch (err) {
        toastError('Failed to send email. Please try again.');
        console.error('Email send error:', err);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <h1 className={`text-xl md:text-2xl font-bold ${darkModeClasses.textPrimary} flex items-center`}>
            <Mail className="h-6 w-6 md:h-7 md:w-7 mr-2 md:mr-3 text-blue-600" />
            <span className="hidden sm:inline">Email Center - </span>{selectedTownName}
          </h1>
          <HelpButton guideId="email/email-templates" variant="icon" />
        </div>
        <p className={`${darkModeClasses.textSecondary} mt-1`}>
          Send bulk emails to family members using Resend or Google SMTP
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className={`${darkModeClasses.container} p-6`}>
            <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Compose Email</h2>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                  Email Template
                </label>
                <select
                  value={messageType}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="general">Custom Email</option>
                  {availableTemplates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                  Recipients
                </label>
                <select
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value)}
                  className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="selected-town">
                    {selectedTownId === 'all' ? 'All Towns' : `${selectedTownName} Family Members`} ({filteredMembers.length})
                  </option>
                  <option value="all-towns">All Towns ({members.filter(m => m.email && m.status !== 'deceased').length})</option>
                  <option value="selected-members">Selected Family Members ({selectedMembers.filter(id => {
                    const m = members.find(m => m.id === id);
                    return m?.email && m.status !== 'deceased';
                  }).length})</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${darkModeClasses.textSecondary} mb-2`}>
                  Message ({message.length} characters)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  rows={8}
                  className={`w-full ${darkModeClasses.input} rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
                />
                <p className={`text-xs ${darkModeClasses.textTertiary} mt-1`}>
                  Use placeholders: [MEMBER_NAME], [DATE], [TIME], [VENUE], [AMOUNT], [ORGANIZATION_NAME]
                </p>
              </div>

              <div className={`${getInfoPanelClasses('blue')} border rounded-lg p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">
                      Recipients: {getRecipientCount()} family members with email
                    </p>
                    <p className="text-sm">
                      Provider: {emailConfig.provider === 'resend' ? 'Resend' : 'Google SMTP'}
                      {!emailConfig.configured && <span className="text-red-600 ml-2">(Not Configured)</span>}
                    </p>
                  </div>
                  <button
                    onClick={handleSendEmail}
                    disabled={!subject.trim() || !message.trim() || getRecipientCount() === 0 || !emailConfig.configured}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <Send className="h-4 w-4" />
                    <span>Send Email</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {recipientType === 'selected-members' && (
            <div className={`${darkModeClasses.container} p-6`}>
              <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Select Family Members</h3>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {filteredMembers.map((member) => {
                  const town = towns.find(t => t.id === member.townId);
                  return (
                    <label key={member.id} className={`flex items-center space-x-3 p-2 ${darkModeClasses.hover} rounded`}>
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMembers([...selectedMembers, member.id]);
                          } else {
                            setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${darkModeClasses.textPrimary}`}>{member.name}</p>
                        <p className={`text-xs ${darkModeClasses.textTertiary}`}>
                          {member.email} • {town?.name}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className={`${darkModeClasses.container} p-6`}>
            <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Email Statistics</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkModeClasses.textSecondary}`}>This Month</span>
                <span className={`text-sm font-semibold ${darkModeClasses.textPrimary}`}>{emailStats.thisMonth.toLocaleString()} emails</span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkModeClasses.textSecondary}`}>Total Sent</span>
                <span className={`text-sm font-semibold ${darkModeClasses.textPrimary}`}>{emailStats.totalSent.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkModeClasses.textSecondary}`}>Delivery Rate</span>
                <span className="text-sm font-semibold text-green-600">{emailStats.deliveryRate.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${darkModeClasses.textSecondary}`}>Total Cost</span>
                <span className="text-sm font-semibold text-red-600">GHS {emailStats.totalCost?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>

          <div className={`${darkModeClasses.container} p-6`}>
            <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>Recent Emails</h3>
            <div className="space-y-3">
              {recentEmails.length > 0 ? recentEmails.map((email) => (
                <div key={email.id} className="border-l-4 border-blue-500 pl-3 py-2">
                  <p className={`text-sm font-medium ${darkModeClasses.textPrimary} line-clamp-1`}>
                    {email.subject}
                  </p>
                  <p className={`text-xs ${darkModeClasses.textSecondary} line-clamp-2 mt-1`}>
                    {email.message}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <div className={`flex items-center space-x-2 text-xs ${darkModeClasses.textTertiary}`}>
                      <Users className="h-3 w-3" />
                      <span>{email.recipients}</span>
                      <Clock className="h-3 w-3" />
                      <span>{new Date(email.date).toLocaleDateString()}</span>
                    </div>
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  </div>
                </div>
              )) : (
                <p className={`text-sm ${darkModeClasses.textSecondary} text-center py-4`}>
                  No recent emails
                </p>
              )}
            </div>
          </div>

          <div className={`${getInfoPanelClasses('yellow')} border rounded-lg p-4`}>
            <h4 className="font-medium mb-2 flex items-center">
              <Settings className="h-4 w-4 mr-2" />
              Email Provider Setup
            </h4>
            <p className="text-sm mb-2">
              Configure your email provider:
            </p>
            <ul className="text-sm space-y-1">
              <li>• Resend API (Recommended)</li>
              <li>• Google SMTP</li>
              <li>• Custom SMTP Server</li>
            </ul>
            <p className="text-xs mt-2 text-gray-600">
              Configure in Developer Settings → Notifications
            </p>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isOpen}
        title={options.title}
        message={options.message}
        confirmText={options.confirmText}
        cancelText={options.cancelText}
        type={options.type}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
};